import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiExternalLink, FiMessageCircle, FiX } from 'react-icons/fi';
import { DiscordPlanKey, getDiscordInviteForPlan } from '../../utils/discordInvites';

type DiscordOnboardingModalProps = {
  plan: DiscordPlanKey;
  onClose: () => void;
  redirectSeconds?: number;
};

const DiscordOnboardingModal: React.FC<DiscordOnboardingModalProps> = ({
  plan,
  onClose,
  redirectSeconds = 10,
}) => {
  const inviteUrl = getDiscordInviteForPlan(plan);
  const [secondsLeft, setSecondsLeft] = useState(redirectSeconds);
  const nowLabel = new Date().toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  useEffect(() => {
    if (!inviteUrl) return;

    const tick = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(tick);
          window.location.href = inviteUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(tick);
  }, [inviteUrl, onClose]);

  if (!inviteUrl) return null;

  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-3 sm:p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#5865F2]/15 text-[#5865F2]">
              <FiMessageCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-foreground">Join Discord onboarding</h3>
              <p className="text-xs text-muted">{planLabel} plan</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-muted transition hover:bg-surface-elevated hover:text-foreground"
            aria-label="Close"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-5">
          <p className="text-sm leading-relaxed text-foreground">
            Please join the onboarding session on Discord to set up Webull.
          </p>

          <div className="rounded-xl border border-border bg-surface-elevated px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">
              Session time
            </p>
            <p className="mt-1 text-sm font-semibold text-foreground">{nowLabel}</p>
          </div>

          <div className="rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 px-4 py-4 text-center">
            <p className="text-xs font-medium text-muted">
              You will redirect in{' '}
              <span className="font-bold tabular-nums text-[#5865F2]">{secondsLeft}</span> sec to
              Discord
            </p>
            <p className="mt-3 text-4xl font-bold tabular-nums text-[#5865F2]">{secondsLeft}</p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-muted transition hover:text-foreground"
          >
            Stay here
          </button>
          <a
            href={inviteUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#4752C4]"
          >
            <FiExternalLink className="h-4 w-4" />
            Join Discord now
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DiscordOnboardingModal;
